import { t } from '@lingui/core/macro';
import { ArrowLeft, Camera } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import type { UploadStage } from '@/hooks/usePhotoUpload';
import { CameraErrorAlert } from './CameraErrorAlert';
import { CaptureActionRail, CaptureRailSecondaryButton, CaptureShutterButton } from './CaptureActionRail';

interface PhotoProgressProps {
  /** Null once the R2 copy is on screen. The local JPEG is released so Android can keep the tab. */
  photo: Blob | null;
  stage: UploadStage;
  /** The photo in R2, already loaded once. Replaces the local photo when set. */
  r2PhotoUrl: string | null;
  error: string | null;
  onCapture: () => void;
  onBack: () => void;
}

/**
 * Covers the step while a photo from the native camera is uploaded and
 * validated. A photo that passes moves on to the next step, so this screen
 * only ever shows progress, or an error with a way to take the photo again.
 */
export function PhotoProgress({ photo, stage, r2PhotoUrl, error, onCapture, onBack }: PhotoProgressProps) {
  const photoUrl = useMemo(() => (photo && !r2PhotoUrl ? URL.createObjectURL(photo) : null), [photo, r2PhotoUrl]);

  useEffect(() => {
    if (!photoUrl) return;
    return () => URL.revokeObjectURL(photoUrl);
  }, [photoUrl]);

  // The local photo until the copy in R2 has loaded, then that copy. Stays up
  // behind an error so the user sees the photo that was rejected.
  const busy = stage !== 'idle';
  const showError = Boolean(error) && !busy;

  return (
    <div className="capture-overlay bg-black">
      <div
        className={`capture-stage absolute inset-0 overflow-hidden flex items-center justify-center ${showError ? 'right-[100px]' : ''}`}
      >
        {(r2PhotoUrl ?? photoUrl) && (
          <img src={r2PhotoUrl ?? photoUrl ?? undefined} alt={t`Captured photo`} className="capture-media-contain" />
        )}

        {busy && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <div className="text-white text-center" role="status">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4" />
              <p className="text-lg">{stage === 'uploading' ? t`Uploading photo...` : t`Validating photo...`}</p>
            </div>
          </div>
        )}

        {showError && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <CameraErrorAlert className="mx-4">{error}</CameraErrorAlert>
          </div>
        )}
      </div>

      {showError && (
        <CaptureActionRail className="absolute top-0 right-0 bottom-0">
          <CaptureShutterButton tone="green" onClick={onCapture} aria-label={t`Capture photo`}>
            <Camera className="w-8 h-8 text-white" />
          </CaptureShutterButton>

          <CaptureRailSecondaryButton onClick={onBack} aria-label={t`Back`}>
            <ArrowLeft className="w-6 h-6 text-white" />
          </CaptureRailSecondaryButton>
        </CaptureActionRail>
      )}
    </div>
  );
}
